import ky from "ky";
import { z } from "zod";
import { useAuthStore } from "./auth";

// Create a custom error for unauthorized access
export class UnauthorizedError extends Error {
  constructor(message = "Unauthorized: Session expired") {
    super(message);
    this.name = "UnauthorizedError";
  }
}
// API client with base URL from environment variables
export const api = ky.create({
  prefixUrl: import.meta.env.VITE_API_URL || "http://localhost:4040/api/v1",
  timeout: 30000,
  credentials: "include", // Include credentials in all requests
  mode: "cors", // Enable CORS
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "Access-Control-Allow-Origin": "*",
  },
  hooks: {
    beforeRequest: [
      (request) => {
        // if (noAuthPaths.includes(window.location.pathname)) {
        //   return;
        // }

        // Get token from auth store
        const authStore = useAuthStore.getState();
        let token = authStore.token;

        // Fallback to localStorage if not in auth store
        if (!token) {
          token = localStorage.getItem("auth_token");
        }

        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        } else {
          console.warn("No auth token available for request");
          // Instead of redirecting here, throw an error that can be handled by the component
          // if (!window.location.pathname.includes("/auth/login")) {
          //   throw new UnauthorizedError("No authentication token found");
          // }
        }

        // Log requests in development
        /* The above TypeScript code is checking if the environment is not in production mode and if
        the DEBUG environment variable is set to "true". If both conditions are met, it will log the
        API request method and URL using `console.debug`, as well as log the request headers by
        converting them to an object using `Object.fromEntries(request.headers.entries())`. This
        code is useful for debugging API requests in a development environment. */
        if (
          import.meta.env.NODE_ENV !== "production" &&
          import.meta.env.DEBUG === "true"
        ) {
          console.debug(`API Request: ${request.method} ${request.url}`);
          console.debug(
            "Request headers:",
            Object.fromEntries(request.headers.entries()),
          );
        }
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        // Handle 401 Unauthorized - clear token and trigger logout
        if (response.status === 401) {
          console.warn(
            "Received 401 Unauthorized - token is invalid or expired",
          );

          // Clear token from localStorage immediately
          localStorage.removeItem("auth_token");

          // Let the auth store handle the logout
          const authStore = useAuthStore.getState();
          if (authStore.isAuthenticated) {
            console.warn("User was authenticated, logging out");
            authStore.logout();
          }

          throw new UnauthorizedError();
        }

        // Handle 400 Bad Request - extract error message from response
        if (response.status === 400) {
          console.warn("Received 400 Bad Request");

          try {
            // Clone the response to read it
            const clonedResponse = response.clone();
            const errorData = await clonedResponse.json();
            console.warn("Error response data:", errorData);

            // Extract detailed error information
            let errorMessage = "Bad Request";

            // Check for error message in various formats
            if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              // Some APIs return errors in an 'error' field
              errorMessage =
                typeof errorData.error === "string"
                  ? errorData.error
                  : "API Error";
            } else if (errorData.detail) {
              // Some APIs use 'detail' for error messages
              errorMessage = errorData.detail;
            }

            // Create error with proper metadata
            const error = new Error(errorMessage) as any;
            error.status = 400;
            error.data = errorData;

            // Add specific error flags based on the error data
            if (errorData.salesforceError) {
              error.salesforceError = true;
              if (!errorMessage || errorMessage === "Bad Request") {
                error.message =
                  "Invalid Salesforce API key or connection refused";
              }
            }

            if (errorData.instantlyError) {
              error.instantlyError = true;
              if (!errorMessage || errorMessage === "Bad Request") {
                error.message =
                  "Invalid Instantly API key or connection refused";
              }
            }

            error.validationError = true;

            console.warn("API validation error:", errorData);
            throw error;
          } catch (parseError) {
            // If we can't parse the JSON, create a more informative error
            console.error("Failed to parse error response:", parseError);
            const error = new Error(
              "Unable to process the server response. Please try again.",
            ) as any;
            error.status = 400;
            error.parseError = true;
            throw error;
          }
        }

        return response;
      },
    ],
  },
  retry: {
    limit: 2,
    methods: ["get"],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
  },
});

// Zod schemas for API request and response types

// Define source type enum first
export const SourceTypeEnum = z.enum([
  "salesforge",
  "instantly",
  "emailbison",
  "smartlead",
]);
export type SourceType = z.infer<typeof SourceTypeEnum>;

// Subscription plan enum
export const SubscriptionPlanEnum = z.enum([
  "free",
  "basic",
  "professional",
  "enterprise",
]);
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanEnum>;

// Subscription plan features
export const SubscriptionFeaturesSchema = z.object({
  maxConnections: z.number(),
  allowedSourceTypes: z.array(SourceTypeEnum),
  maxResponses: z.number().optional(),
  advancedAnalytics: z.boolean().optional(),
  prioritySupport: z.boolean().optional(),
});
export type SubscriptionFeatures = z.infer<typeof SubscriptionFeaturesSchema>;

// Subscription schema
export const UserSubscriptionSchema = z.object({
  plan: SubscriptionPlanEnum,
  features: SubscriptionFeaturesSchema,
  expiresAt: z.string().datetime().optional(),
});
export type UserSubscription = z.infer<typeof UserSubscriptionSchema>;

export const UserSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  jobTitle: z.string().nullable().optional(),
  onboardingComplete: z.boolean().optional(),
  onboardingStep: z.string().optional(),
  subscription: UserSubscriptionSchema.optional(),
  // createdAt: z.string().datetime(),
  // updatedAt: z.string().datetime()
});

export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

export const RegisterRequestSchema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  password: z.string().min(8),
});

export const ForgotPasswordRequestSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(1, "Reset token is required"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export const AuthResponseSchema = z.object({
  user: UserSchema,
  token: z.string(),
  message: z.string().optional(),
});
export const AuthMeResponseSchema = z.object({
  user: UserSchema,
});

// Source type enum already defined above

export const EmailSchema = z.object({
  id: z.number(),
  userId: z.number().optional(),
  externalId: z.string().optional(),
  sourceType: SourceTypeEnum.optional(),
  connectionId: z.string().optional(),
  sender: z.string(),
  recipient: z.string(),
  subject: z.string(),
  body: z.string().optional(),
  receivedAt: z.string().datetime(),
  processingStatus: z
    .enum([
      "pending",
      "processing",
      "completed",
      "failed",
      "insufficient_credits",
      "error",
    ])
    .optional(),
  aiResponse: z.string().nullable().optional(),
  confidenceScore: z.number().nullable().optional(),
  handled: z.boolean().default(false).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});

export const EmailsResponseSchema = z.object({
  emails: z.array(EmailSchema),
  pagination: z.object({
    limit: z.number(),
    offset: z.number(),
    total: z.number(),
  }),
});
// Create a frontend-friendly version of the email with additional UI metadata
export const EmailUISchema = EmailSchema.extend({
  senderName: z.string().optional(),
  confidence: z.enum(["high", "medium", "low", "pending"]).optional(),
  hasResponse: z.boolean(),
  sourceLabel: z.string().optional(), // Display name for the source
  sourceIcon: z.string().optional(), // Icon identifier for the source
});

export const EmailStatsSchema = z.object({
  stats: z.array(
    z.object({
      month: z.string(),
      totalEmails: z.number(),
      processedEmails: z.number(),
      pendingEmails: z.number(),
      completedEmails: z.number(),
      failedEmails: z.number(),
      sourceBreakdown: z.array(
        z.object({
          sourceType: SourceTypeEnum,
          count: z.number(),
        }),
      ),
    }),
  ),
});

// Subscription schemas
export const PlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  price: z.number(),
  currency: z.string(),
  interval: z.enum(["month", "year"]),
  credits: z.number(),
  emailReplies: z.number(),
  features: z.array(z.string()),
  isCurrent: z.boolean().optional(),
});

export const SubscriptionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  planId: z.string(),
  stripeSubscriptionId: z.string().optional(),
  status: z.enum(["active", "canceled", "past_due", "unpaid", "trialing"]),
  currentPeriodStart: z.string().datetime(),
  currentPeriodEnd: z.string().datetime(),
  cancelAtPeriodEnd: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const CheckoutSessionSchema = z.object({
  url: z.string().url(),
});

// Onboarding schemas
export const OnboardingStepEnum = z.enum([
  "welcome",
  "providers",
  "connection-select",
  "salesforce",
  "instantly",
  "company",
  "tone",
  "calendar",
  "complete",
  "completed",
]);

export const OnboardingStatusSchema = z.object({
  userId: z.number(),
  currentStep: OnboardingStepEnum,
  isComplete: z.boolean(),
  progress: z.number(),
});

export const CompanyContextSchema = z.object({
  companyName: z.string().min(1),
  companyDescription: z.string().min(1),
  companyUrl: z.string().url().optional().nullable(),
  hyperlinkCompanyName: z.boolean().default(false),
  productDetails: z.string().optional().nullable(),
  targetAudience: z.string().optional().nullable(),
  sellingPoints: z.string().optional().nullable(),
  commonObjections: z.string().optional().nullable(),
  preferredIntent: z
    .enum(["MEETING_FIRST", "DISCOVERY_FIRST", "PITCH_FIRST", "QUALIFY_FIRST"])
    .default("MEETING_FIRST"),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});

export const TonePreferenceSchema = z.object({
  tonePreference: z.enum([
    "professional",
    "friendly",
    "concise",
    "detailed",
    "custom",
  ]),
  customToneDescription: z.string().nullable().optional(),
});

export const CalendarIntegrationSchema = z.object({
  calendarType: z.string().optional(),
  bookingLink: z.string().url(),
  customCalendarType: z.string().optional(),
});

// Base connection schema for API responses
export const ConnectionSchema = z.object({
  id: z.string(),
  userId: z.number(),
  sourceType: SourceTypeEnum,
  name: z.string().nullable(),
  workspace: z.string(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  apiKey: z.string(),
  // Auto-reply configuration
  autoReply: z.boolean().optional(),
  minReplyDelaySeconds: z.number().nullable().optional(),
  maxReplyDelaySeconds: z.number().nullable().optional(),
});

// Schema for creating new connections
export const CreateConnectionSchema = z.object({
  sourceType: SourceTypeEnum,
  name: z.string().nullable().optional(),
  apiKey: z.string().min(1),
  workspace: z.string().min(1),
  isActive: z.boolean().optional().default(true),
  autoReply: z.boolean().optional().default(false),
  minReplyDelaySeconds: z.number().nullable().optional().default(null),
  maxReplyDelaySeconds: z.number().nullable().optional().default(null),
});

// Schema for updating existing connections
export const UpdateConnectionSchema = z.object({
  name: z.string().nullable().optional(),
  apiKey: z.string().min(1).optional(),
  workspace: z.string().min(1).optional(),
  isActive: z.boolean().optional(),
  // Add auto-reply fields to update schema
  autoReply: z.boolean().optional(),
  minReplyDelaySeconds: z.number().nullable().optional(),
  maxReplyDelaySeconds: z.number().nullable().optional(),
});

// Source-specific connection schemas
export const SalesforceConnectionSchema = ConnectionSchema.extend({
  sourceType: z.literal("salesforge"),
});

export const InstantlyConnectionSchema = ConnectionSchema.extend({
  sourceType: z.literal("instantly"),
});

export const OnboardingDataSchema = z.object({
  status: OnboardingStatusSchema,
  companyContext: CompanyContextSchema.nullable(),
  tonePreferences: TonePreferenceSchema.nullable(),
  calendarIntegration: CalendarIntegrationSchema.nullable(),
  salesforceConnection: SalesforceConnectionSchema.nullable(),
});

// Type definitions from Zod schemas
export type User = z.infer<typeof UserSchema>;
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;
export type AuthResponse = z.infer<typeof AuthResponseSchema>;
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;
export type Email = z.infer<typeof EmailSchema>;
export type EmailsResponse = z.infer<typeof EmailsResponseSchema>;
export type EmailUI = z.infer<typeof EmailUISchema>;
export type EmailStats = z.infer<typeof EmailStatsSchema>;
export type Plan = z.infer<typeof PlanSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
export type CheckoutSession = z.infer<typeof CheckoutSessionSchema>;
export type OnboardingStep = z.infer<typeof OnboardingStepEnum>;
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;
export type CompanyContext = z.infer<typeof CompanyContextSchema>;
export type TonePreference = z.infer<typeof TonePreferenceSchema>;
export type CalendarIntegration = z.infer<typeof CalendarIntegrationSchema>;
export type Connection = z.infer<typeof ConnectionSchema>;
export type CreateConnection = z.infer<typeof CreateConnectionSchema>;
export type UpdateConnection = z.infer<typeof UpdateConnectionSchema>;
export type SalesforceConnection = z.infer<typeof SalesforceConnectionSchema>;
export type InstantlyConnection = z.infer<typeof InstantlyConnectionSchema>;
export type OnboardingData = z.infer<typeof OnboardingDataSchema>;

// Generic API response wrapper
export const APIResponseSchema = <T extends z.ZodType>(schema: T) =>
  z.object({
    success: z.boolean(),
    data: schema,
  });

// Update OnboardingData schema to use the wrapper
export const OnboardingDataResponseSchema =
  APIResponseSchema(OnboardingDataSchema);
export type OnboardingDataResponse = z.infer<
  typeof OnboardingDataResponseSchema
>;

// Profile update request schema
export const ProfileUpdateSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  company: z.string().optional(),
  jobTitle: z.string().optional(),
});

export type ProfileUpdateRequest = z.infer<typeof ProfileUpdateSchema>;

// Password change request schema
export const PasswordChangeSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z
    .string()
    .min(8, "New password must be at least 8 characters long"),
});

export type PasswordChangeRequest = z.infer<typeof PasswordChangeSchema>;

// API functions
export const authApi = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post("auth/login", { json: data }).json();
    return AuthResponseSchema.parse(response);
  },

  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post("auth/register", { json: data }).json();
    return AuthResponseSchema.parse(response);
  },

  getMe: async (options?: { refresh?: boolean }): Promise<User> => {
    const response = await api
      .get("auth/me", {
        searchParams: options?.refresh ? { refresh: "true" } : undefined,
      })
      .json();
    const payload = AuthMeResponseSchema.parse(response);
    return payload.user;
  },

  forgotPassword: async (
    data: ForgotPasswordRequest,
  ): Promise<{ message: string }> => {
    const response = await api
      .post("auth/forgot-password", { json: data })
      .json<{ message: string }>();
    return { message: response.message || "Password reset email sent" };
  },

  resetPassword: async (
    data: ResetPasswordRequest,
  ): Promise<{ message: string }> => {
    const response = await api
      .post("auth/reset-password", { json: data })
      .json<{ message: string }>();
    return { message: response.message || "Password reset successful" };
  },

  updateProfile: async (data: ProfileUpdateRequest): Promise<User> => {
    const response = await api.patch("auth/profile", { json: data }).json();
    const payload = AuthMeResponseSchema.parse(response);
    return payload.user;
  },

  changePassword: async (
    data: PasswordChangeRequest,
  ): Promise<{ message: string }> => {
    const response = await api
      .post("auth/change-password", { json: data })
      .json<{ message: string }>();
    return { message: response.message || "Password changed successfully" };
  },
};

export const connectionsApi = {
  // Get all connections
  getConnections: async (): Promise<Connection[]> => {
    const response = await api
      .get("connections")
      .json<{ connections: Connection[] }>();
    return z.array(ConnectionSchema).parse(response.connections);
  },

  // Get a specific connection
  getConnection: async (id: string): Promise<Connection> => {
    const response = await api
      .get(`connections/${id}`)
      .json<{ data: Connection }>();
    return ConnectionSchema.parse(response.data);
  },

  // Create a new connection
  createConnection: async (data: CreateConnection): Promise<Connection> => {
    // Set default values for min and max delay if autoReply is true but no values provided
    const updatedData = {
      ...data,
      minReplyDelaySeconds:
        data.autoReply && !data.minReplyDelaySeconds
          ? 60
          : data.minReplyDelaySeconds,
      maxReplyDelaySeconds:
        data.autoReply && !data.maxReplyDelaySeconds
          ? 300
          : data.maxReplyDelaySeconds,
    };

    const response = await api
      .post("connections", { json: updatedData })
      .json<{ data: Connection }>();
    return ConnectionSchema.parse(response.data);
  },

  // Update an existing connection
  updateConnection: async (
    id: string,
    data: UpdateConnection,
  ): Promise<Connection> => {
    // Set default values for min and max delay if autoReply is true but no values provided
    const updatedData = {
      ...data,
      minReplyDelaySeconds:
        data.autoReply && !data.minReplyDelaySeconds
          ? 60
          : data.minReplyDelaySeconds,
      maxReplyDelaySeconds:
        data.autoReply && !data.maxReplyDelaySeconds
          ? 300
          : data.maxReplyDelaySeconds,
    };

    const response = await api
      .put(`connections/${id}`, { json: updatedData })
      .json<{ connection: Connection }>();
    return ConnectionSchema.parse(response.connection);
  },

  // Delete a connection
  deleteConnection: async (
    id: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api
      .delete(`connections/${id}`)
      .json<{ success: boolean; message: string }>();
    return response;
  },

  // Test a connection without saving it
  testConnection: async (
    data: CreateConnection,
  ): Promise<{ success: boolean; message: string; error?: string }> => {
    const response = await api
      .post("connections/test", { json: data })
      .json<{ success: boolean; message: string; error?: string }>();
    return response;
  },
};

export const emailsApi = {
  getEmails: async (
    sourceType?: SourceType,
    options?: {
      limit?: number;
      offset?: number;
      status?: string;
      search?: string;
    },
  ): Promise<EmailsResponse> => {
    const params = new URLSearchParams();

    if (sourceType) {
      params.append("source", sourceType);
    }

    if (options?.limit) {
      params.append("limit", options.limit.toString());
    }

    if (options?.status) {
      params.append("status", options.status);
    }

    // Use the search endpoint if search query is provided
    const endpoint = options?.search ? "emails/search" : "emails";

    // Add search parameter only for search endpoint
    if (options?.search) {
      params.append("search", options.search);
    } else if (options?.offset) {
      // Only use offset for non-search requests
      params.append("offset", options.offset.toString());
    }

    const queryString = params.toString();
    const url = `${endpoint}${queryString ? `?${queryString}` : ""}`;

    const response = await api.get(url).json();
    try {
      return EmailsResponseSchema.parse(response);
    } catch (error) {
      console.error("error", error);
      return {
        emails: [],
        pagination: {
          limit: options?.limit || 0,
          offset: options?.offset || 0,
          total: 0,
        },
      };
    }
  },

  getEmail: async (id: string): Promise<Email> => {
    const response = await api.get(`emails/${id}`).json();
    return EmailSchema.parse(response);
  },

  markAsHandled: async (id: string): Promise<Email> => {
    const response = await api.post(`emails/${id}/handled`).json();
    return EmailSchema.parse(response);
  },

  // Function to transform API email to UI email format
  transformEmailsForUI: (emails: Email[]): EmailUI[] => {
    return emails.map((email) => {
      // Extract name from email format like "Mike Johnson <mike.johnson@example.com>"
      const senderName = email.sender.includes("<")
        ? email.sender.split("<")[0].trim()
        : email.sender.split("@")[0];

      // Determine confidence based on confidenceScore
      let confidence: "high" | "medium" | "low" | "pending" = "pending";
      const score = email.confidenceScore ?? null;

      if (email.processingStatus === "completed" && score !== null) {
        if (score >= 85) {
          confidence = "high";
        } else if (score >= 70) {
          confidence = "medium";
        } else {
          confidence = "low";
        }
      } else if (email.processingStatus === "failed") {
        confidence = "low";
      }

      // Set source-specific display information
      let sourceLabel = "Unknown";
      let sourceIcon = "mail";

      if (email.sourceType === "salesforge") {
        sourceLabel = "Salesforge";
        sourceIcon = "cloud";
      } else if (email.sourceType === "instantly") {
        sourceLabel = "Instantly";
        sourceIcon = "zap";
      } else if (email.sourceType === "emailbison") {
        sourceLabel = "Emailbison";
        sourceIcon = "mail";
      }

      return {
        ...email,
        senderName,
        confidence,
        hasResponse:
          email.processingStatus === "completed" && !!email.aiResponse,
        sourceLabel,
        sourceIcon,
      };
    });
  },

  getEmailStats: async (sourceType?: SourceType): Promise<EmailStats> => {
    if (!sourceType) {
      const response = await api.get("emails/stats/monthly").json();
      return EmailStatsSchema.parse(response);
    }
    const url = `emails/stats/monthly?source=${sourceType}`;
    const response = await api.get(url).json();
    return EmailStatsSchema.parse(response);
  },
};

export const onboardingApi = {
  // Get current onboarding status
  getOnboardingStatus: async (): Promise<OnboardingStatus> => {
    try {
      const response = await api
        .get("onboarding/status")
        .json<{ data: OnboardingStatus }>();
      return OnboardingStatusSchema.parse(response.data);
    } catch (error: any) {
      console.error("Error fetching onboarding status:", error);
      if (error.status === 401) {
        throw new Error("Authentication required. Please log in again.");
      }
      if (error.status === 404) {
        throw new Error(
          "Onboarding status not found. Please start onboarding.",
        );
      }
      throw error;
    }
  },

  // Get all onboarding data
  getOnboardingData: async (): Promise<OnboardingDataResponse> => {
    try {
      const token = localStorage.getItem("auth_token");

      const response = await api
        .get("onboarding/data", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .json<OnboardingDataResponse>();

      // Validate response structure
      if (!response || typeof response !== "object") {
        throw new Error("Invalid response format");
      }

      if (!response.data) {
        throw new Error("Response missing data field");
      }

      const parsed = OnboardingDataResponseSchema.parse(response);
      return parsed;
    } catch (error: any) {
      console.error("Error fetching onboarding data:", error);
      if (error.status === 401) {
        throw new Error("Authentication required. Please log in again.");
      }
      if (error.status === 404) {
        throw new Error(
          "Onboarding data not found. Please complete onboarding first.",
        );
      }
      if (error.name === "ZodError") {
        console.error("Schema validation error:", error.errors);
        throw new Error("Invalid data format received from server");
      }
      throw error;
    }
  },

  // Update current onboarding step
  updateOnboardingStep: async (
    step: OnboardingStep,
  ): Promise<OnboardingStatus> => {
    const response = await api
      .put("onboarding/status", { json: { step } })
      .json<{ data: OnboardingStatus }>();
    return OnboardingStatusSchema.parse(response.data);
  },

  // Advance to next step
  advanceToNextStep: async (): Promise<OnboardingStatus> => {
    const response = await api
      .post("onboarding/next")
      .json<{ data: OnboardingStatus }>();
    return OnboardingStatusSchema.parse(response.data);
  },

  // Mark onboarding as complete
  completeOnboarding: async (): Promise<OnboardingStatus> => {
    const response = await api
      .post("onboarding/complete")
      .json<{ data: OnboardingStatus }>();
    return OnboardingStatusSchema.parse(response.data);
  },

  // Save Salesforce API connection
  saveSalesforceConnection: async (
    data: SalesforceConnection,
  ): Promise<OnboardingStatus> => {
    try {
      const response = await api
        .post("onboarding/salesforce", { json: data })
        .json<{ data: OnboardingStatus }>();
      return OnboardingStatusSchema.parse(response.data);
    } catch (error: any) {
      console.error("Error saving Salesforce connection:", error);
      // Enhance error with more context
      if (error.status === 400) {
        error.message =
          error.data?.message || "Invalid Salesforce API key or settings";
        error.salesforceError = true;
      }
      throw error;
    }
  },

  // Save Instantly API connection
  saveInstantlyConnection: async (
    data: InstantlyConnection,
  ): Promise<OnboardingStatus> => {
    try {
      const response = await api
        .post("onboarding/instantly", { json: data })
        .json<{ data: OnboardingStatus }>();
      return OnboardingStatusSchema.parse(response.data);
    } catch (error: any) {
      console.error("Error connecting to Instantly:", error);
      // Enhance error with more context
      if (error.status === 400) {
        error.message =
          error.data?.message || "Invalid Instantly API key or settings";
        error.instantlyError = true;
      }
      throw error;
    }
  },

  // Save company context
  saveCompanyContext: async (
    data: CompanyContext,
  ): Promise<OnboardingStatus> => {
    const response = await api
      .post("onboarding/company", { json: data })
      .json<{ data: OnboardingStatus }>();
    return OnboardingStatusSchema.parse(response.data);
  },

  // Save tone preferences
  saveTonePreferences: async (
    data: TonePreference,
  ): Promise<OnboardingStatus> => {
    const response = await api
      .post("onboarding/tone", { json: data })
      .json<{ data: OnboardingStatus }>();
    return OnboardingStatusSchema.parse(response.data);
  },

  // Save calendar integration
  saveCalendarIntegration: async (
    data: CalendarIntegration,
  ): Promise<OnboardingStatus> => {
    const response = await api
      .post("onboarding/calendar", { json: data })
      .json<{ data: OnboardingStatus }>();
    return OnboardingStatusSchema.parse(response.data);
  },
};

// Add subscription API functions
export const subscriptionApi = {
  // Get current subscription features and limits
  getFeatures: async (): Promise<SubscriptionFeatures> => {
    const response = await api.get("subscriptions/features").json();
    return SubscriptionFeaturesSchema.parse(response);
  },

  // Get available subscription plans
  getPlans: async (): Promise<Plan[]> => {
    const response = await api.get("subscriptions/plans").json();
    return z.array(PlanSchema).parse(response);
  },

  // Get credit balance and transaction history
  getCredits: async (options?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    balance: number;
    history: {
      id: number;
      amount: number;
      balance: number;
      source: string;
      description: string;
      createdAt: string;
    }[];
    pagination: {
      limit: number;
      offset: number;
    };
  }> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());

    const response = await api
      .get(
        `subscriptions/credits${params.toString() ? `?${params.toString()}` : ""}`,
      )
      .json();
    return z
      .object({
        balance: z.number(),
        history: z.array(
          z.object({
            id: z.number(),
            amount: z.number(),
            balance: z.number(),
            source: z.string(),
            description: z.string(),
            createdAt: z.string(),
          }),
        ),
        pagination: z.object({
          limit: z.number(),
          offset: z.number(),
        }),
      })
      .parse(response);
  },

  // Get available credit packs
  getCreditPacks: async (): Promise<{
    creditPacks: {
      id: string;
      name: string;
      credits: number;
      price: number;
    }[];
  }> => {
    const response = await api.get("subscriptions/credit-packs").json();
    return z
      .object({
        creditPacks: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
            credits: z.number(),
            price: z.number(),
          }),
        ),
      })
      .parse(response);
  },
};
